// src/features/wbs/editor/treeFlat.ts
import type { DraftItem, FlatRow, KeyId, ParentKey } from '../types.local';
import { keyOf } from '../types.local';

/** 옵션 */
export type FlattenOptions = {
  /** 루트 부모 키 (기본값: null) */
  rootParent?: ParentKey;
  /** 정렬: 기본 true (orderIndex → name → keyId) */
  sort?: boolean;
  /** depth 시작값 (기본 0) */
  baseDepth?: number;
};

/** 내부 정렬 비교 함수: orderIndex → name → keyId */
function cmpForSiblings(a: DraftItem, b: DraftItem): number {
  const ai = a.orderIndex ?? 0;
  const bi = b.orderIndex ?? 0;
  if (ai !== bi) return ai - bi;

  const an = a.name || '';
  const bn = b.name || '';
  if (an !== bn) return an.localeCompare(bn);

  const ak = keyOf(a);
  const bk = keyOf(b);
  if (ak !== bk) return ak.localeCompare(bk);
  return 0;
}

/** 아이템의 부모 키를 통일해서 가져온다 (parentKey ?? parentNodeId ?? null) */
function getParentKey(it: DraftItem): ParentKey {
  return (it.parentKey ?? it.parentNodeId ?? null) as ParentKey;
}

/** children map: parentKey → DraftItem[] */
function buildChildrenMap(items: DraftItem[]): Map<ParentKey, DraftItem[]> {
  const map = new Map<ParentKey, DraftItem[]>();
  for (const it of items) {
    const p = getParentKey(it);
    const bucket = map.get(p);
    if (bucket) bucket.push(it);
    else map.set(p, [it]);
  }
  return map;
}

/** index map: keyId → DraftItem */
function buildIndex(items: DraftItem[]): Map<KeyId, DraftItem> {
  const idx = new Map<KeyId, DraftItem>();
  for (const it of items) idx.set(keyOf(it), it);
  return idx;
}

/** 하나의 DraftItem을 FlatRow로 변환 */
function toFlat(it: DraftItem, depth: number): FlatRow {
  return {
    nodeId: it.nodeId ?? null,
    // cid: it.cid,
    parentNodeId: it.parentNodeId ?? null,
    parentKey: it.parentKey ?? (it.parentNodeId ?? null),
    depth,
    orderIndex: it.orderIndex ?? null,
    name: it.name,
    code: it.code ?? null,
    level: it.level ?? null,
    plannedStart: it.plannedStart ?? null,
    plannedFinish: it.plannedFinish ?? null,
    durationDays: it.durationDays ?? null,
    weight: it.weight ?? null,
    memo: it.memo ?? null,
    assignee: it.assignee ?? null,
    publishedListVersion: it.publishedListVersion ?? null,
    isDeleted: !!it.isDeleted,
    cid: (it as any).cid ?? undefined,
  };
}

/**
 * 트리를 평탄화하여 FlatRow[] 반환.
 * - parentKey/parentNodeId 혼용 지원
 * - 정렬: orderIndex → name → keyId (옵션)
 */
export function flattenTree(items: DraftItem[], opts: FlattenOptions = {}): FlatRow[] {
  const { rootParent = null, sort = true, baseDepth = 0 } = opts;

  // 방어적 복사(정렬 영향 최소화)
  const list = [...items];

  // parentKey 기준 children map
  const childrenMap = buildChildrenMap(list);

  // 정렬 옵션 적용
  if (sort) {
    for (const [, arr] of childrenMap) {
      arr.sort(cmpForSiblings);
    }
  }

  const out: FlatRow[] = [];

  const dfs = (parent: ParentKey, depth: number) => {
    const kids = childrenMap.get(parent);
    if (!kids || kids.length === 0) return;

    for (const child of kids) {
      out.push(toFlat(child, depth));
      // const childKey = (child.parentKey ?? child.parentNodeId ?? null) as ParentKey;
      // child 자신의 key를 다음 단계의 parent로 써야 하므로 다시 계산
      // const nextParent: ParentKey = keyOf(child);
      // 만약 map이 parentKey 기반으로만 쌓여 있다면 keyOf(child)를 parent로 넘겨야 자식이 매칭됨
      const nextParent: ParentKey = keyOf(child);
      dfs(nextParent, depth + 1);
    }
  };

  dfs(rootParent, baseDepth);
  return out;
}

/**
 * 루트부터의 경로를 KeyId 배열로 계산 (예: 선택된 노드까지 확장 경로)
 * - 고아 노드(부모 누락)를 만나면 거기서 중단
 */
export function buildPathToRoot(id: KeyId, items: DraftItem[]): KeyId[] {
  const index = buildIndex(items);
  const path: KeyId[] = [];

  let cur: DraftItem | undefined = index.get(id);
  const guard = new Set<KeyId>(); // 순환 참조 방지

  while (cur) {
    const curId = keyOf(cur);
    if (guard.has(curId)) break;
    guard.add(curId);
    path.push(curId);

    const pKey = getParentKey(cur);
    if (pKey === null) break;

    // parentKey가 KeyId라면 그걸로, 아니라면 nodeId 매칭 시도
    const next = index.get(pKey) ?? index.get((cur.parentNodeId ?? '') as KeyId);
    if (!next) break;
    cur = next;
  }

  // 루트 → id 순서로 정렬
  return path.reverse();
}

/**
 * 특정 KeyId의 서브트리 집합을 반환 (자기 자신 포함)
 */
export function collectSubtreeKeys(rootKey: KeyId, items: DraftItem[]): Set<KeyId> {
  const childrenMap = buildChildrenMap(items);
  const res = new Set<KeyId>();
  const guard = new Set<KeyId>();

  const dfs = (k: KeyId) => {
    if (guard.has(k)) return;
    guard.add(k);
    res.add(k);

    const children = childrenMap.get(k) ?? []; // parentKey가 keyId인 경우
    // parentNodeId만 있는 데이터도 보호 차원에서 한번 더 스캔
    const fallback = children.length
      ? []
      : (childrenMap.get((items.find(x => keyOf(x) === k)?.nodeId ?? null) as ParentKey) ?? []);

    for (const ch of [...children, ...fallback]) {
      dfs(keyOf(ch));
    }
  };

  dfs(rootKey);
  return res;
}

/**
 * orderIndex가 누락되었거나 중복일 때 안전한 10,20,30… 시퀀스를 부여한
 * 새로운 DraftItem 배열을 반환 (원본 불변)
 */
export function normalizeSiblingOrders(items: DraftItem[]): DraftItem[] {
  const childrenMap = buildChildrenMap(items);

  const out: DraftItem[] = [];
  for (const arr of childrenMap.values()) {
    // 원소 복사
    const copy = arr.map(x => ({ ...x }));
    // 정렬 후 10,20,30… 재부여
    copy.sort(cmpForSiblings).forEach((it, idx) => {
      it.orderIndex = (idx + 1) * 10;
    });
    out.push(...copy);
  }
  return out;
}
